import { HistoryStack } from '../core/history';
import { BufferPool } from '../gl/bufferPool';
import { PingPong } from '../gl/framebuffer';
import { RenderTarget } from '../gl/renderTarget';
import { SelectionMask } from '../selection/mask';
import type { EngineHistoryOp } from './historyOps';
import { TextureSurface, type LayerSurface } from './surfaces';

export interface LayerResources {
  surface: LayerSurface;
  /** Layer mask, allocated only when the user adds one. */
  mask: RenderTarget | null;
  /**
   * Unfiltered pixels for a smart object. Null on ordinary layers. The surface
   * holds a cached filtered view when filters are present; the compositor
   * prefers this original and replays filters each frame.
   */
  smartSource: RenderTarget | null;
}

/** Recycled scratch buffers the tree compositor borrows for one frame. */
export interface CompositeBuffers {
  color: BufferPool;
  coverage: BufferPool;
}

/** CPU copy of every pixel a document owns, used to move it to a new context. */
export interface ResourceSnapshot {
  width: number;
  height: number;
  layers: {
    layerId: string;
    pixels: Uint8Array;
    mask: Uint8Array | null;
    smartPixels: Uint8Array | null;
  }[];
  channels: { channelId: string; pixels: Uint8Array }[];
  selection: Uint8Array;
}

const HISTORY_MAX_BYTES = 1024 * 1024 * 1024;
const HISTORY_MAX_STEPS = 50;

/**
 * All GPU-side state for one open document. Kept out of the React store so that
 * re-rendering the UI never touches textures.
 */
export class DocumentResources {
  readonly history = new HistoryStack<EngineHistoryOp>({
    maxBytes: HISTORY_MAX_BYTES,
    maxSteps: HISTORY_MAX_STEPS,
  });
  readonly selection: SelectionMask;
  readonly pingPong: PingPong;
  /** Pools shared by every composite of this document. */
  readonly buffers: CompositeBuffers;

  private readonly layers = new Map<string, LayerResources>();
  private readonly alphaChannels = new Map<string, RenderTarget>();
  private readonly rgbaScratch: (RenderTarget | undefined)[] = [];
  private readonly r8Scratch: (RenderTarget | undefined)[] = [];
  private readonly fieldScratch: (RenderTarget | undefined)[] = [];
  private w: number;
  private h: number;

  constructor(
    private readonly gl: WebGL2RenderingContext,
    width: number,
    height: number,
  ) {
    this.w = width;
    this.h = height;
    this.selection = new SelectionMask(gl, width, height);
    this.pingPong = new PingPong(gl, width, height, 'rgba8');
    this.buffers = {
      color: new BufferPool(gl, 'rgba8', width, height),
      coverage: new BufferPool(gl, 'r8', width, height),
    };
  }

  get width(): number {
    return this.w;
  }

  get height(): number {
    return this.h;
  }

  ensureLayer(layerId: string): LayerResources {
    const existing = this.layers.get(layerId);
    if (existing) return existing;
    const created: LayerResources = {
      surface: new TextureSurface(this.gl, this.w, this.h),
      mask: null,
      smartSource: null,
    };
    this.layers.set(layerId, created);
    return created;
  }

  /** Preserved original for smart-object filter replay. */
  ensureSmartSource(layerId: string): RenderTarget {
    const layer = this.ensureLayer(layerId);
    if (!layer.smartSource) {
      layer.smartSource = new RenderTarget(this.gl, this.w, this.h, 'rgba8', 'linear');
      layer.smartSource.clear(0, 0, 0, 0);
    } else {
      layer.smartSource.resize(this.w, this.h);
    }
    return layer.smartSource;
  }

  clearSmartSource(layerId: string): void {
    const layer = this.layers.get(layerId);
    if (!layer?.smartSource) return;
    layer.smartSource.dispose();
    layer.smartSource = null;
  }

  layer(layerId: string): LayerResources | undefined {
    return this.layers.get(layerId);
  }

  ensureMask(layerId: string): RenderTarget {
    const layer = this.ensureLayer(layerId);
    if (!layer.mask) {
      layer.mask = new RenderTarget(this.gl, this.w, this.h, 'r8', 'linear');
      layer.mask.clear(1, 1, 1, 1);
    }
    return layer.mask;
  }

  removeMask(layerId: string): void {
    const layer = this.layers.get(layerId);
    if (!layer?.mask) return;
    layer.mask.dispose();
    layer.mask = null;
  }

  /**
   * Alpha channel storage, allocated on first use. Channels start black, the way
   * a new channel in Photoshop selects nothing.
   */
  ensureChannel(channelId: string): RenderTarget {
    const existing = this.alphaChannels.get(channelId);
    if (existing) {
      existing.resize(this.w, this.h);
      return existing;
    }
    const created = new RenderTarget(this.gl, this.w, this.h, 'r8', 'linear');
    created.clear(0, 0, 0, 0);
    this.alphaChannels.set(channelId, created);
    return created;
  }

  channel(channelId: string): RenderTarget | undefined {
    return this.alphaChannels.get(channelId);
  }

  removeChannel(channelId: string): void {
    const target = this.alphaChannels.get(channelId);
    if (!target) return;
    target.dispose();
    this.alphaChannels.delete(channelId);
  }

  deleteLayer(layerId: string): void {
    const layer = this.layers.get(layerId);
    if (!layer) return;
    layer.surface.dispose();
    layer.mask?.dispose();
    layer.smartSource?.dispose();
    this.layers.delete(layerId);
  }

  /** Document-sized RGBA scratch target, allocated on first use. */
  rgba(slot: number): RenderTarget {
    let target = this.rgbaScratch[slot];
    if (!target) {
      target = new RenderTarget(this.gl, this.w, this.h, 'rgba8', 'linear');
      this.rgbaScratch[slot] = target;
    } else {
      target.resize(this.w, this.h);
    }
    return target;
  }

  /**
   * Document-sized scratch target for signed intermediates, such as the
   * correction field of the healing solve.
   */
  field(slot: number): RenderTarget {
    let target = this.fieldScratch[slot];
    if (!target) {
      target = new RenderTarget(this.gl, this.w, this.h, 'rgba16f', 'linear');
      this.fieldScratch[slot] = target;
    } else {
      target.resize(this.w, this.h);
    }
    return target;
  }

  /** Document-sized single-channel scratch target, allocated on first use. */
  r8(slot: number): RenderTarget {
    let target = this.r8Scratch[slot];
    if (!target) {
      target = new RenderTarget(this.gl, this.w, this.h, 'r8', 'linear');
      this.r8Scratch[slot] = target;
    } else {
      target.resize(this.w, this.h);
    }
    return target;
  }

  snapshotPixels(): ResourceSnapshot {
    const layers = [...this.layers].map(([layerId, layer]) => ({
      layerId,
      pixels: layer.surface.readPixels(),
      mask: layer.mask ? layer.mask.texture.readPixels() : null,
      smartPixels: layer.smartSource ? layer.smartSource.texture.readPixels() : null,
    }));
    const channels = [...this.alphaChannels].map(([channelId, target]) => ({
      channelId,
      pixels: target.texture.readPixels(),
    }));
    return {
      width: this.w,
      height: this.h,
      layers,
      channels,
      selection: this.selection.snapshot(),
    };
  }

  /** Refills a freshly built set of resources from `snapshotPixels`. */
  restorePixels(snapshot: ResourceSnapshot): void {
    const rect = { x: 0, y: 0, width: snapshot.width, height: snapshot.height };
    for (const layer of snapshot.layers) {
      this.ensureLayer(layer.layerId).surface.writePixels(rect, layer.pixels);
      if (layer.mask) this.ensureMask(layer.layerId).texture.uploadPixels(rect, layer.mask);
      if (layer.smartPixels) {
        this.ensureSmartSource(layer.layerId).texture.uploadPixels(rect, layer.smartPixels);
      }
    }
    for (const channel of snapshot.channels) {
      this.ensureChannel(channel.channelId).texture.uploadPixels(rect, channel.pixels);
    }
    this.selection.restore(snapshot.selection);
  }

  /** Resizes every surface. Callers are responsible for restoring pixel content. */
  resize(width: number, height: number): void {
    this.w = width;
    this.h = height;
    for (const layer of this.layers.values()) {
      layer.surface.resize(width, height);
      layer.mask?.resize(width, height);
      layer.smartSource?.resize(width, height);
    }
    for (const target of this.alphaChannels.values()) target.resize(width, height);
    this.selection.resize(width, height);
    this.pingPong.resize(width, height);
    this.buffers.color.resize(width, height);
    this.buffers.coverage.resize(width, height);
    for (const target of this.rgbaScratch) target?.resize(width, height);
    for (const target of this.r8Scratch) target?.resize(width, height);
    for (const target of this.fieldScratch) target?.resize(width, height);
  }

  get bytes(): number {
    let total =
      this.selection.bytes +
      this.pingPong.width * this.pingPong.height * 8 +
      this.buffers.color.bytes +
      this.buffers.coverage.bytes;
    for (const layer of this.layers.values()) {
      total += layer.surface.bytes + (layer.mask?.bytes ?? 0);
    }
    for (const target of this.alphaChannels.values()) total += target.bytes;
    for (const target of this.rgbaScratch) total += target?.bytes ?? 0;
    for (const target of this.r8Scratch) total += target?.bytes ?? 0;
    for (const target of this.fieldScratch) total += target?.bytes ?? 0;
    return total;
  }

  dispose(): void {
    for (const layer of this.layers.values()) {
      layer.surface.dispose();
      layer.mask?.dispose();
      layer.smartSource?.dispose();
    }
    this.layers.clear();
    for (const target of this.alphaChannels.values()) target.dispose();
    this.alphaChannels.clear();
    for (const target of this.rgbaScratch) target?.dispose();
    for (const target of this.r8Scratch) target?.dispose();
    for (const target of this.fieldScratch) target?.dispose();
    this.rgbaScratch.length = 0;
    this.r8Scratch.length = 0;
    this.fieldScratch.length = 0;
    this.selection.dispose();
    this.pingPong.dispose();
    this.buffers.color.dispose();
    this.buffers.coverage.dispose();
    this.history.clear();
  }
}
